import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { liveStationForSource, type CountryConfig, type NewsItem } from "@orvis/core";
import type { FirestoreLike } from "@orvis/data";
import { Fragment, useMemo } from "react";
import { LoadingRow, NewsCard } from "../components/ui.js";
import { InlineAd } from "../components/InlineAd.js";
import { Chip, ChipRow } from "../components/Chips.js";
import { inlineAdAfter } from "../ads.js";
import { useChannels, useNews, useRadios } from "../queries.js";
import { useNewsPrefs } from "../state/newsPrefs.js";
import { useNowPlaying } from "../state/nowPlaying.js";

export function NewsScreen({ db, country }: { db: FirestoreLike; country: CountryConfig }) {
  const { t } = useTranslation();
  const q = useNews(db, country);
  const prefs = useNewsPrefs();
  const watchLive = useWatchLive(db, country, q.data?.source?.sources);

  const allItems = q.data?.items ?? [];
  const sources = useMemo(() => {
    const names: string[] = [];
    for (const n of allItems) if (n.source && !names.includes(n.source)) names.push(n.source);
    return names;
  }, [allItems]);

  if (q.isPending) return <LoadingRow label={t("news")} />;
  if (q.isError) {
    return (
      <div className="p-4 text-text/70 text-sm">
        {String(q.error)}
      </div>
    );
  }
  if (allItems.length === 0) {
    return <div className="p-4 text-text/70 text-sm">{t("no_news_for_now")}</div>;
  }
  const items = prefs.visible(allItems);
  return (
    <div className="p-3 space-y-3">
      {sources.length >= 2 ? (
        <ChipRow label={t("news_sources")}>
          <Chip active={prefs.hiddenCount === 0} onClick={prefs.showAll}>
            {t("filter_all")}
          </Chip>
          {sources.map((s) => (
            <Chip key={s} active={!prefs.isHidden(s)} onClick={() => prefs.toggle(s)}>
              {s}
            </Chip>
          ))}
        </ChipRow>
      ) : null}
      {items.length === 0 ? (
        <div className="p-4 text-text/70 text-sm text-center">{t("news_all_hidden")}</div>
      ) : null}
      {items.map((n, idx) => (
        <Fragment key={n.id}>
          <NewsCard item={n} action={watchLive(n)} />
          {inlineAdAfter(idx, items.length) ? <InlineAd country={country} slot="news" /> : null}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * "Watch live" / "Listen live" for stories whose outlet also broadcasts —
 * resolves the outlet to a playable station (see liveStationForSource) and
 * renders a button that starts it on the right tab.
 */
export function useWatchLive(db: FirestoreLike, country: CountryConfig, docSources: CountryConfig["newsSources"] | undefined) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const now = useNowPlaying();
  const tv = useChannels(db, country.code).data?.open ?? [];
  const radio = useRadios(db, country.code).data?.open ?? [];
  const sources = docSources && docSources.length > 0 ? docSources : country.newsSources;

  return (item: NewsItem) => {
    const match = liveStationForSource(item.source, sources, tv, radio);
    if (!match) return undefined;
    return (
      <button
        type="button"
        onClick={() => {
          if (match.kind === "tv") now.playTv(match.station);
          else now.playRadio(match.station);
          navigate(match.kind === "tv" ? "/tv" : "/radio");
        }}
        className="inline-flex items-center gap-1 rounded-full bg-primary border border-accent px-3 py-1 text-xs text-text hover:brightness-125 focus-visible:brightness-125"
        aria-label={`${match.kind === "tv" ? t("watch_live") : t("listen_live")}: ${match.station.name}`}
      >
        <span aria-hidden className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        {match.kind === "tv" ? t("watch_live") : t("listen_live")}
      </button>
    );
  };
}
